import { Button, Cell, Grid, Heading, Icon, Tooltip } from 'bold-ui'
import { useSession } from 'components/auth/useSession'
import { useErrorHandler } from 'components/error'
import { PecFormApi } from 'components/form'
import { LoadingIndicator } from 'components/loading'
import { CidadaoTerritorio } from 'graphql/types.generated'
import { useEffect, useMemo, useState } from 'react'
import Permissions from 'types/Permissions'
import { MetaArray } from 'util/metaPath'
import { v4 } from 'uuid'
import { useEditableListField } from 'view/atendimentos/detail/components/EditableList'

import {
  convertCidadaoTerritorioToCidadao,
  convertResideDesdeMonthYearToResideDesde,
  convertResideDesdeToResideDesdeMonthYear,
} from '../converter-cadastroImovel'
import { useGetCallbackUrlToCadastroImovel } from '../hooks/useGetCallbackUrlToCadastroImovel'
import {
  CadastrarEditarFamiliaFormModel,
  CadastroImovelCacheState,
  CidadaoCadastroImovel,
  FamiliaFormModel,
  UpdateCadastroImovelCacheState,
} from '../model-cadastroImovel'
import {
  mudancaResponsavelFamiliar,
  possuiCidadaoPresenteOutrasFamilias,
  setCadastroFamiliaCacheState,
} from '../utils-cadastroImovel'
import { CadastrarEditarFamiliaModal } from './modal/CadastrarEditarFamiliaModal'
import { NucleoFamiliarWrapper } from './NucleoFamiliarWrapper'

interface FamiliaImovelSectionProps {
  name: MetaArray<FamiliaFormModel>
  cidadaoModal: CidadaoCadastroImovel
  cacheState: CadastroImovelCacheState
  shouldRenderCache: boolean
  dataColeta: string
  disabled: boolean
  cidadaosImovel: ReadonlyArray<CidadaoTerritorio>
  loadingBuscaCidadaos: boolean
  isImovelInativo: boolean
  onChange: UpdateCadastroImovelCacheState
  onScrollAfterRedirect(): void
}

export const FamiliaImovelSection = ({
  name,
  cacheState,
  shouldRenderCache,
  cidadaoModal,
  dataColeta,
  disabled,
  cidadaosImovel,
  loadingBuscaCidadaos,
  isImovelInativo,
  onChange,
  onScrollAfterRedirect,
}: FamiliaImovelSectionProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [familiaIdEdit, setFamiliaIdEdit] = useState<ID>(
    cacheState?.familiaCidadaoModalState?.cadastroFamiliaModal?.editionId ?? null
  )

  const handleRejection = useErrorHandler()

  const { hasAuthorization } = useSession()

  const {
    handleSubmit,
    handleRowChanged,
    removeItem,
    input: { value },
  } = useEditableListField({
    name,
  })

  const handleEditFamilia = (familia: FamiliaFormModel) => handleRowChanged(familia)
  const handleRemoveFamilia = (familia: FamiliaFormModel) => removeItem(familia)

  const handleDeleteCadastroFamiliaCache = () =>
    onChange((previous) => setCadastroFamiliaCacheState(null, false, previous))

  const handleCadastroFamiliaSaveCache = (form: CadastrarEditarFamiliaFormModel) =>
    onChange((previous) => setCadastroFamiliaCacheState(form, true, previous, familiaIdEdit))

  const handleCloseModal = () => {
    setIsModalOpen(false)
    setFamiliaIdEdit(null)
    handleDeleteCadastroFamiliaCache()
    onScrollAfterRedirect()
  }

  const handleCadastrarFamilia = (
    familia: CadastrarEditarFamiliaFormModel,
    formApi: PecFormApi<CadastrarEditarFamiliaFormModel>
  ) => {
    handleSubmit(
      {
        ...familia,
        _id: v4(),
        resideDesde: convertResideDesdeMonthYearToResideDesde(familia.resideDesdeMonthYear),
        isNovoCadastro: true,
        isResponsavelCadastrado: true,
        isResponsavelDeclarado: true,
        isMudouSe: false,
        cidadaos: [
          {
            _id: null,
            id: familia.responsavel.id,
            mudouSe: false,
            registroObito: null,
            isNovoCadastroNaFamilia: true,
          },
        ],
        responsavelId: familia.responsavel.id,
      },
      formApi
    )
      .then(() => onScrollAfterRedirect())
      .catch(handleRejection)
  }

  const handleEditarFamilia = (values: CadastrarEditarFamiliaFormModel) => {
    const familiaAtualizadaNovoResponsavel = mudancaResponsavelFamiliar(familia, values.responsavel)
    const { numeroMembros, numeroProntuarioFamiliar, rendaFamiliar, resideDesdeMonthYear } = values

    handleRowChanged({
      ...familiaAtualizadaNovoResponsavel,
      numeroMembros,
      numeroProntuarioFamiliar,
      rendaFamiliar,
      resideDesde: convertResideDesdeMonthYearToResideDesde(resideDesdeMonthYear),
    })
  }

  const cidadaos = convertCidadaoTerritorioToCidadao(cidadaosImovel)

  const cidadaoPresenteFamilias = (familias: Array<FamiliaFormModel>) => {
    return (cidadaoId: ID) => possuiCidadaoPresenteOutrasFamilias(familias || [], cidadaoId, familia?._id)
  }

  const cadastrarEEditar = hasAuthorization(Permissions.gestaoDeCadastros.visualizarCadastroIndividual.cadastrarEEditar)
  const imovelId = cacheState?.form?.id

  const cadastroImovelPath = useGetCallbackUrlToCadastroImovel(imovelId)

  const familia: FamiliaFormModel = (value || []).find((familia) => familia._id === familiaIdEdit)
  const cidadaoResponsavel = cidadaos?.find((cidadao) => cidadao.id === familia?.responsavelId)

  const familiaCidadaoModalState = cacheState?.familiaCidadaoModalState
  const renderCacheFamilia = shouldRenderCache && !!familiaCidadaoModalState?.cadastroFamiliaModal?.modalOpen

  const cadastrarEditarFamiliaInitialValues = useMemo(
    () =>
      renderCacheFamilia
        ? { ...familiaCidadaoModalState?.cadastroFamiliaModal?.form, responsavel: cidadaoModal }
        : setEditionInitialValues(familia, cidadaoResponsavel),
    [cidadaoModal, cidadaoResponsavel, familia, familiaCidadaoModalState, renderCacheFamilia]
  )

  useEffect(() => {
    if (renderCacheFamilia || familiaIdEdit !== null) {
      setIsModalOpen(true)
    }
  }, [renderCacheFamilia, familiaIdEdit])

  return (
    <Grid>
      <Cell size={3}>
        <Heading level={1}>Famílias</Heading>
      </Cell>
      <Cell size={8} />
      <Cell size={4}>
        <Tooltip text={isImovelInativo ? 'Não é possível editar famílias em um imóvel inativo.' : ''}>
          <Button
            onClick={() => setIsModalOpen(true)}
            kind='primary'
            skin='outline'
            disabled={value?.length > 99 || disabled || isImovelInativo}
          >
            <Icon icon='plus' /> Adicionar familia ao imóvel
          </Button>
        </Tooltip>
      </Cell>
      <Cell size={9} />
      {loadingBuscaCidadaos ? (
        <Cell size={12}>
          <LoadingIndicator />
        </Cell>
      ) : (
        <Cell size={12}>
          {Array.isArray(value) &&
            value?.map((familia: FamiliaFormModel, index: number) => (
              <NucleoFamiliarWrapper
                key={familia._id}
                familia={familia}
                cidadaoModal={cidadaoModal}
                cidadaosImovel={cidadaos}
                cacheState={cacheState}
                shouldRenderCache={shouldRenderCache}
                cadastroImovelPath={cadastroImovelPath}
                name={name.get(index).cidadaos}
                hasPermission={cadastrarEEditar}
                isCidadaoPresenteFamilias={cidadaoPresenteFamilias(value)}
                onEditarFamilia={handleEditFamilia}
                onRemoverFamilia={handleRemoveFamilia}
                onChange={onChange}
                onScrollAfterRedirect={onScrollAfterRedirect}
                onSetFamiliaIdEdit={setFamiliaIdEdit}
                dataColeta={dataColeta}
                disabled={disabled}
                isImovelInativo={isImovelInativo}
              />
            ))}
        </Cell>
      )}
      {isModalOpen && (
        <CadastrarEditarFamiliaModal
          open={isModalOpen}
          hasPermission={cadastrarEEditar}
          initialValues={cadastrarEditarFamiliaInitialValues}
          cidadaoCallBackUrl={cadastroImovelPath}
          imovelId={cacheState?.form?.id}
          dataColeta={dataColeta}
          isCidadaoPresenteFamilias={cidadaoPresenteFamilias(value)}
          onClose={handleCloseModal}
          onCadastrarFamilia={handleCadastrarFamilia}
          onEditFamilia={handleEditarFamilia}
          onCadastroFamiliaSaveCache={handleCadastroFamiliaSaveCache}
        />
      )}
    </Grid>
  )
}

const setEditionInitialValues = (
  familia: FamiliaFormModel,
  responsavel: CidadaoCadastroImovel
): CadastrarEditarFamiliaFormModel => {
  if (familia)
    return {
      ...familia,
      resideDesdeMonthYear: convertResideDesdeToResideDesdeMonthYear(familia.resideDesde),
      responsavel: responsavel,
    }
  return null
}
